import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TeamDisplayData,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useMemo, useRef } from 'react'
import { listMembersDisplayPage, listTeamsDisplayPage } from '../pagedIdentitySearch'

export type SearchResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type TenantMemberSearchFn = (params: { search?: string }) => Promise<SearchResults>

/**
 * Teams and members are two lists shown as two groups, teams above members.
 * Each side carries its own page number, plus whether it has nothing left to
 * give — once it is done its request is skipped rather than repeated.
 */
type CombinedPageToken = {
  team?: number
  member?: number
  doneTeams?: boolean
  doneMembers?: boolean
}

type CombinedSearchPage = {
  teams: TeamDisplayData[]
  tenantMembers: TenantMemberDisplayData[]
  next: CombinedPageToken | null
}

/**
 * Debounced federated search for autocomplete fields that accept teams and members.
 *
 * Same duplicate-prevention as {@link useSearchTenantMembers}: `selectedMembersIds`
 * are filtered out of both team and member result lists before they reach the UI
 * unless the field is paging, in which case selected matches stay in the result
 * set so Autocomplete does not treat a selection as a new option list.
 */
const useSearchTenantMembersAndTeams = (
  /** Current autocomplete input; query runs only when this is non-empty, unless paging. */
  search: string,
  /** Recipient IDs already chosen in the form; excluded from dropdown options when not paging. */
  selectedMembersIds: string[] = [],
  /** Injected search API (e.g. `listRecognitionRecipients`). Unused when `paginated`. */
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>,
  /** When set, forwarded to `searchFn` for delegated recognition recipient search. */
  delegatorId?: string,
  paginated: boolean = false,
  open: boolean = false,
) => {
  const lastMatches = useRef<FederatedSearchRecipientsResults | undefined>(undefined)
  const searchTerm = search || undefined

  const federatedQuery = useQuery({
    queryKey: ['searchTenantMembersAndTeams', search, delegatorId],
    queryFn: async () => {
      const result = await searchFn({ search, delegatorId })
      return result
    },
    placeholderData: lastMatches.current,
    enabled: !paginated && !isEmpty(search),
  })

  if (federatedQuery.isSuccess) {
    lastMatches.current = federatedQuery.data
  }

  const pagedQuery = useInfiniteQuery({
    queryKey: ['searchTenantMembersAndTeams', 'paged', searchTerm],
    queryFn: async ({ pageParam }: { pageParam: CombinedPageToken | undefined }) => {
      // The two sides are paged one after the other rather than together. Both
      // groups growing at once would slot each new team in above the members
      // already on screen, and since the listbox holds its scroll position the
      // rows under the user would jump backwards — out of Users and into Teams —
      // at the very moment they scrolled for more.
      const fetchTeams = !pageParam?.doneTeams
      const teams = fetchTeams
        ? await listTeamsDisplayPage({ search: searchTerm, page: pageParam?.team })
        : { results: [] as TeamDisplayData[], next: null }
      const doneTeams = !fetchTeams || teams.next === null

      // The round that exhausts the teams goes on to ask for the first page of
      // members, so the usual case of teams fitting in a single page still opens
      // with both groups filled in.
      const fetchMembers = doneTeams && !pageParam?.doneMembers
      const members = fetchMembers
        ? await listMembersDisplayPage({ search: searchTerm, page: pageParam?.member })
        : { results: [] as TenantMemberDisplayData[], next: null }
      const doneMembers = fetchMembers ? members.next === null : Boolean(pageParam?.doneMembers)

      const page: CombinedSearchPage = {
        teams: teams.results,
        tenantMembers: members.results,
        next:
          doneTeams && doneMembers
            ? null
            : {
                team: teams.next ?? pageParam?.team,
                member: members.next ?? pageParam?.member,
                doneTeams,
                doneMembers,
              },
      }
      return page
    },
    initialPageParam: undefined as CombinedPageToken | undefined,
    getNextPageParam: page => page.next,
    placeholderData: keepPreviousData,
    enabled: paginated && (open || Boolean(searchTerm)),
    staleTime: 60 * 1000,
  })

  const searchedTenantMembersAndTeams = useMemo(() => {
    if (paginated) {
      // Only pages up to the one that exhausted the teams carry teams, and only
      // that page onwards carries members, so grouping the two kinds this way
      // preserves page order and each new page lands at the end of the list.
      const teams = pagedQuery.data?.pages.flatMap(page => page.teams) ?? []
      const members = pagedQuery.data?.pages.flatMap(page => page.tenantMembers) ?? []
      return [...teams, ...members]
    }

    if (isEmpty(search) || !federatedQuery.data) {
      return []
    }

    const teamMatches =
      federatedQuery.data.teams?.filter(match => !selectedMembersIds.find(id => id === match.id)) ||
      []
    const memberMatches =
      federatedQuery.data.tenantMembers?.filter(
        match => !selectedMembersIds.find(id => id === match.id),
      ) || []
    return [...teamMatches, ...memberMatches]
  }, [paginated, pagedQuery.data, federatedQuery.data, search, selectedMembersIds])

  return {
    searchedTenantMembersAndTeams,
    isFetching: paginated ? pagedQuery.isFetching : federatedQuery.isFetching,
    isFetchingNextPage: paginated ? pagedQuery.isFetchingNextPage : false,
    hasNextPage: paginated ? Boolean(pagedQuery.hasNextPage) : false,
    fetchNextPage: pagedQuery.fetchNextPage,
  }
}

export default useSearchTenantMembersAndTeams
